(() => {
  'use strict';

  if (window.__sillonPdpLoaded) return;
  window.__sillonPdpLoaded = true;

  const mediaQuery = window.matchMedia('(max-width: 999px)');
  const controllers = new Map();

  const isEditable = (element) => Boolean(
    element && (
      element.matches('input, textarea, select') ||
      element.isContentEditable
    )
  );

  class SillonPdpController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.getAttribute('data-sillon-pdp');
      this.inlineVisible = false;
      this.keyboardFocused = false;
      this.syncFrame = 0;
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onFocusChange = this.onFocusChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.init();
    }

    init() {
      this.refreshElements();
      this.observeInlinePanel();
      this.observeRoot();
      document.addEventListener('focusin', this.onFocusChange);
      document.addEventListener('focusout', this.onFocusChange);
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.addEventListener('orientationchange', this.onViewportChange);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.root.addEventListener('variant:change', this.onVariantChange);
      mediaQuery.addEventListener?.('change', this.onViewportChange);
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      this.scheduleSync();
      this.onScroll();
    }

    refreshElements() {
      this.rail = this.root.querySelector('[data-sillon-rail-anchor]');
      this.inlinePanel = this.root.querySelector(`[data-sillon-inline-atc="${this.sectionId}"]`);
      this.realButton = this.inlinePanel?.querySelector('[name="add"]') || this.inlinePanel?.querySelector('[type="submit"]') || null;
      this.sticky = document.querySelector(`[data-sillon-sticky-atc="${this.sectionId}"]`);
      this.stickyButton = this.sticky?.querySelector('[data-sillon-sticky-button]') || null;

      if (this.stickyButton && !this.stickyButton.dataset.sillonClickBound) {
        this.stickyButton.dataset.sillonClickBound = 'true';
        this.stickyButton.addEventListener('click', () => {
          this.refreshElements();
          if (this.realButton && !this.realButton.disabled) this.realButton.click();
        });
      }
    }

    observeRoot() {
      this.mutationObserver?.disconnect();
      this.mutationObserver = new MutationObserver(() => this.scheduleSync());
      this.mutationObserver.observe(this.root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'class', 'hidden']
      });
    }

    observeInlinePanel() {
      this.inlineObserver?.disconnect();
      if (!this.inlinePanel) return;
      const rect = this.inlinePanel.getBoundingClientRect();
      this.inlineVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      this.inlineObserver = new IntersectionObserver((entries) => {
        this.inlineVisible = Boolean(entries[0]?.isIntersecting);
        this.updateStickyVisibility();
      }, { threshold: 0.01 });
      this.inlineObserver.observe(this.inlinePanel);
    }

    onScroll() {
      if (!this.rail) this.rail = this.root.querySelector('[data-sillon-rail-anchor]');
      if (!this.rail) return;
      if (!mediaQuery.matches) {
        this.rail.classList.remove('is-pinned');
        this.rail.style.removeProperty('--sillon-rail-height');
        return;
      }
      if (!this.rail.classList.contains('is-pinned')) {
        this.railNaturalTop = this.rail.getBoundingClientRect().top + window.scrollY;
        this.rail.style.setProperty('--sillon-rail-height', `${Math.ceil(this.rail.getBoundingClientRect().height)}px`);
      }
      const pinTop = 0;
      const shouldPin = window.scrollY + pinTop >= this.railNaturalTop;
      this.rail.classList.toggle('is-pinned', shouldPin);
    }

    onViewportChange() {
      this.keyboardFocused = isEditable(document.activeElement);
      if (this.rail) this.rail.classList.remove('is-pinned');
      this.onScroll();
      this.scheduleSync();
    }

    onFocusChange() {
      window.requestAnimationFrame(() => {
        this.keyboardFocused = isEditable(document.activeElement);
        this.updateStickyVisibility();
      });
    }

    onVariantChange(event) {
      const amount = this.root.querySelector('[data-sillon-installment-amount]');
      const price = Number(event.detail?.variant?.price);
      const count = Number(amount?.getAttribute('data-sillon-installment-count'));
      if (!amount || !Number.isFinite(price) || !Number.isFinite(count) || count < 2) return;

      const locale = window.Shopify?.locale || document.documentElement.lang || 'fr-FR';
      const currency = window.Shopify?.currency?.active || 'EUR';
      amount.textContent = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(price / count / 100);
      this.scheduleSync();
    }

    scheduleSync() {
      window.cancelAnimationFrame(this.syncFrame);
      this.syncFrame = window.requestAnimationFrame(() => this.sync());
    }

    sync() {
      const previousInlinePanel = this.inlinePanel;
      this.refreshElements();
      if (previousInlinePanel !== this.inlinePanel) this.observeInlinePanel();
      if (!this.sticky || !this.stickyButton || !this.inlinePanel || !this.realButton) {
        this.setStickyVisible(false);
        return;
      }

      const inlinePrice = this.inlinePanel.querySelector('[data-sillon-inline-price]');
      const inlineShipping = this.inlinePanel.querySelector('[data-sillon-inline-shipping]');
      const stickyPrice = this.sticky.querySelector('[data-sillon-sticky-price]');
      const stickyShipping = this.sticky.querySelector('[data-sillon-sticky-shipping]');
      const stickyLabel = this.sticky.querySelector('[data-sillon-sticky-label]');
      const realLabel = this.realButton.querySelector('[data-sillon-atc-text]') || this.realButton;

      if (inlinePrice && stickyPrice) stickyPrice.innerHTML = inlinePrice.outerHTML;
      if (inlineShipping && stickyShipping) stickyShipping.innerHTML = inlineShipping.outerHTML;
      if (stickyLabel) stickyLabel.textContent = (realLabel.textContent || '').trim();
      this.stickyButton.disabled = this.realButton.disabled;
      this.stickyButton.setAttribute('aria-label', stickyLabel?.textContent || '');
      this.updateStickyVisibility();
    }

    updateStickyVisibility() {
      const visible = mediaQuery.matches &&
        Boolean(this.inlinePanel && this.realButton) &&
        !this.inlineVisible &&
        !this.keyboardFocused;
      this.setStickyVisible(visible);
    }

    setStickyVisible(visible) {
      if (!this.sticky) return;
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      document.body.classList.toggle('sillon-sticky-visible', visible);
      if (visible) {
        window.requestAnimationFrame(() => {
          const height = Math.ceil(this.sticky.getBoundingClientRect().height);
          document.body.style.setProperty('--sillon-sticky-height', `${height}px`);
        });
      } else {
        document.body.style.removeProperty('--sillon-sticky-height');
      }
    }

    destroy() {
      this.setStickyVisible(false);
      this.inlineObserver?.disconnect();
      this.mutationObserver?.disconnect();
      window.cancelAnimationFrame(this.syncFrame);
      document.removeEventListener('focusin', this.onFocusChange);
      document.removeEventListener('focusout', this.onFocusChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('orientationchange', this.onViewportChange);
      window.removeEventListener('scroll', this.onScroll);
      this.root.removeEventListener('variant:change', this.onVariantChange);
      mediaQuery.removeEventListener?.('change', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    }
  }

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-sillon-pdp]').forEach((root) => {
      const id = root.getAttribute('data-sillon-pdp');
      if (controllers.has(id)) controllers.get(id).destroy();
      controllers.set(id, new SillonPdpController(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-sillon-pdp]');
    const id = root?.getAttribute('data-sillon-pdp');
    if (!id || !controllers.has(id)) return;
    controllers.get(id).destroy();
    controllers.delete(id);
  });
})();
